import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv, txr } from "../lib/tx";

/*
 * "Read introduction" content for the MFA fatigue course.
 * Three slides, each with a bespoke flat inline-SVG illustration that carries
 * one subtle animation (pulsing prompt, blinking cursor, expanding ring).
 */

/* ------------------------------------------------------------------ */
/* 1. a phone getting repeated push prompts                            */
/* ------------------------------------------------------------------ */
function RequestCard({ y, n, time, faded }: { y: number; n: number; time: string; faded?: boolean }): JSX.Element {
  return (
    <g opacity={faded ? 0.6 : 1}>
      <rect x="160.5" y={y + 0.5} width="183" height="47" rx="9" fill={C.panel2} stroke={C.line} />
      <circle cx="180" cy={y + 24} r="11" fill={C.mint} fillOpacity="0.14" />
      <text x="180" y={y + 28} fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {txv(n)}
      </text>
      <text x="198" y={y + 21} fontSize="12" fontWeight="700" fill={C.text}>
        {tx("Sign-in request")}
      </text>
      <text x="198" y={y + 36} fontSize="10.5" fill={C.muted}>
        {txr("{time} · Amsterdam", { time: txv(time) })}
      </text>
    </g>
  );
}

function PushBurstArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: requests you did not start")} aria={tx("A phone showing an Approve sign-in prompt with a pulsing Approve button, next to three sign-in requests that arrived in two minutes")} height={H}>
      <rect x="20.5" y="38.5" width="125" height="244" rx="17" fill={C.code} stroke={C.line} />
      <rect x="66" y="45" width="34" height="5" rx="2.5" fill={C.line} />
      <rect x="29.5" y="62.5" width="107" height="132" rx="10" fill={C.panel2} stroke={C.line} />
      <AppChip x={37} y={70} />
      <text x="67" y="86" fontSize="10.5" fontWeight="700" fill={C.text}>
        {tx("Authenticator")}
      </text>
      <text x="37" y="116" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Approve sign-in?")}
      </text>
      <text x="37" y="132" fontSize="10.5" fill={C.muted}>
        {tx("Amsterdam, NL")}
      </text>
      <rect x="37.5" y="150.5" width="44" height="26" rx="7" fill="none" stroke={C.line} />
      <text x="59.5" y="167" fontSize="11" fontWeight="600" fill={C.soft} textAnchor="middle">
        {tx("Deny")}
      </text>
      <rect className="sr-anim-pulse" x="86.5" y="150.5" width="44" height="26" rx="7" fill={C.amber} fillOpacity="0.16" stroke={C.amber} style={{ transformOrigin: "108.5px 163.5px" }} />
      <text x="108.5" y="167" fontSize="11" fontWeight="700" fill={C.amber} textAnchor="middle">
        {tx("Approve")}
      </text>
      <rect x="29.5" y="206.5" width="107" height="30" rx="8" fill={C.panel2} stroke={C.line} strokeOpacity="0.6" />
      <rect x="29.5" y="244.5" width="107" height="30" rx="8" fill={C.panel2} stroke={C.line} strokeOpacity="0.35" />

      <text x="160" y="52" fontSize="10.5" fill={C.muted}>
        {tx("Three requests in two minutes")}
      </text>
      <RequestCard y={60} n={1} time="09:03" faded />
      <RequestCard y={114} n={2} time="09:04" faded />
      <RequestCard y={168} n={3} time="09:05" />

      <rect x="160.5" y="226.5" width="183" height="54" rx="9" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="177" cy="253.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="177" cy="253.5" r="1.8" fill={C.amber} />
      <text x="190" y="249" fontSize="11" fontWeight="700" fill={C.amber}>
        {tx("You did not start these")}
      </text>
      <text x="190" y="264" fontSize="10.5" fill={C.soft}>
        {tx("Someone is hoping you tap")}
      </text>
      <text x="190" y="276" fontSize="10.5" fill={C.soft}>
        {tx("Approve just to stop them.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. number matching: the number on YOUR screen, not a caller's       */
/* ------------------------------------------------------------------ */
function NumberMatchArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: number matching")} aria={tx("A sign-in screen showing the number 42 and the phone asking you to type it, with a caller asking you to type a different number, 68, which is a trap")} height={H}>
      <rect x="16.5" y="38.5" width="158" height="118" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="28" y="57" fontSize="10.5" fill={C.muted}>
        {tx("Your own screen")}
      </text>
      <text x="28" y="74" fontSize="11.5" fontWeight="700" fill={C.text}>
        {tx("Approve sign-in")}
      </text>
      <rect x="55.5" y="86.5" width="80" height="52" rx="9" fill={C.code} stroke={C.mint} strokeOpacity="0.5" />
      <text x="95.5" y="124" fontSize="30" fontWeight="700" fontFamily={MONO} fill={C.mint} textAnchor="middle">
        42
      </text>
      <circle className="sr-anim-ring" cx="95.5" cy="112.5" r="30" fill="none" stroke={C.mint} strokeWidth="1.2" strokeOpacity="0.6" />

      <line x1="174" y1="97" x2="190" y2="97" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" />
      <polyline points="185,92 190,97 185,102" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

      <rect x="190.5" y="38.5" width="153" height="118" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="202" y="57" fontSize="10.5" fill={C.muted}>
        {tx("Your phone")}
      </text>
      <text x="202" y="74" fontSize="11.5" fontWeight="700" fill={C.text}>
        {tx("Enter the number")}
      </text>
      <rect x="202.5" y="88.5" width="60" height="34" rx="8" fill={C.code} stroke={C.line} />
      <text x="212" y="112" fontSize="18" fontWeight="700" fontFamily={MONO} fill={C.text}>
        42
      </text>
      <rect className="sr-anim-blink" x="238" y="96" width="1.5" height="18" rx="0.75" fill={C.mint} />
      <rect x="270.5" y="88.5" width="64" height="34" rx="8" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="302.5" y="109" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Approve")}
      </text>
      <text x="202" y="142" fontSize="10.5" fill={C.muted}>
        {tx("Matches the screen in front of you")}
      </text>

      <rect x="16.5" y="170.5" width="327" height="110" rx="10" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="176" width="3" height="99" rx="1.5" fill={C.red} />
      <circle cx="41" cy="196" r="10" fill={C.red} fillOpacity="0.16" />
      <path d="M38 193 l6 6 M44 193 l-6 6" stroke={C.red} strokeWidth="1.7" strokeLinecap="round" />
      <text x="58" y="200" fontSize="12.5" fontWeight="700" fill={C.red}>
        {tx("A caller says: \"Type 68\"")}
      </text>
      <rect x="30" y="214" width="299" height="28" rx="6" fill={C.code} />
      <text x="40" y="232" fontSize="11" fontFamily={MONO} fill={C.soft}>
        {tx("\"IT Service Desk\" · +41 44 500 21 90")}
      </text>
      <text x="30" y="258" fontSize="11" fill={C.soft}>
        {tx("That number comes from the attacker's own sign-in.")}
      </text>
      <text x="30" y="272" fontSize="11" fill={C.soft}>
        {tx("Never type a number someone gives you.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. deny, then report                                                */
/* ------------------------------------------------------------------ */
function StepRow({ y, n, title, sub, children }: { y: number; n: number; title: string; sub: string; children?: JSX.Element }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="72" rx="10" fill={C.panel2} stroke={C.line} />
      <circle cx="40" cy={y + 36} r="13" fill={C.mint} fillOpacity="0.14" />
      <text x="40" y={y + 41} fontSize="13" fontWeight="700" fill={C.mint} textAnchor="middle">
        {txv(n)}
      </text>
      <text x="64" y={y + 31} fontSize="13" fontWeight="700" fill={C.text}>
        {txv(title)}
      </text>
      <text x="64" y={y + 48} fontSize="10.5" fill={C.muted}>
        {txv(sub)}
      </text>
      {txv(children)}
    </g>
  );
}

function DenyReportArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("What to do")} aria={tx("Three steps: deny the request, report it to IT Security, and check with IT on a number you already know")} height={H}>
      <StepRow y={34} n={1} title={tx("Deny the request")} sub={tx("You did not start it, so say no.")}>
        <g>
          <rect className="sr-anim-pulse" x="270" y="52" width="58" height="34" rx="9" fill={C.mint} fillOpacity="0.14" stroke={C.mint} style={{ transformOrigin: "299px 69px" }} />
          <text x="299" y="73" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
            {tx("Deny")}
          </text>
        </g>
      </StepRow>
      <StepRow y={114} n={2} title={tx("Report it")} sub={tx("Tell IT Security someone tried your account.")}>
        <g>
          <rect x="262.5" y="132.5" width="66" height="34" rx="9" fill={C.code} stroke={C.line} />
          <text x="295.5" y="153" fontSize="12" fontWeight="700" fill={C.text} textAnchor="middle">
            {tx("Report")}
          </text>
        </g>
      </StepRow>
      <StepRow y={194} n={3} title={tx("Check, then change")} sub={tx("Call IT on a number you already know.")}>
        <g>
          <rect x="262.5" y="212.5" width="66" height="34" rx="9" fill={C.code} stroke={C.line} />
          <path d="M281 235 c0 -9 4 -13 8 -14 l3 5 -3 3 c1 3 3 5 6 6 l3 -3 5 3 c-1 4 -5 8 -14 8 -4 0 -8 -4 -8 -8 z" fill="none" stroke={C.mint} strokeWidth="1.4" strokeLinejoin="round" />
          <circle className="sr-anim-ring" cx="312" cy="229.5" r="5" fill="none" stroke={C.mint} strokeWidth="1.4" />
          <circle cx="312" cy="229.5" r="1.8" fill={C.mint} />
        </g>
      </StepRow>
    </Frame>
  );
}

export const MFA_FATIGUE_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "A password is not the only lock, but you hold the key",
    sections: [
      {
        h: "What this course is about",
        body: "Multi-factor sign-in sends a request to your phone. Attackers who already have your password try to wear you down with request after request until you approve one.",
      },
      {
        h: "Who it is for",
        body: "Everyone who signs in with an authenticator app, and especially people in finance who handle payments and reports.",
      },
      {
        h: "Why you are the control",
        body: "No tool can tell your sign-in from an attacker\u2019s. Only you know whether you just tried to log in. That makes your tap the last line of defence.",
      },
    ],
    illustration: PushBurstArt,
    caption: "Several requests in a row, and you started none of them.",
  },
  {
    tab: "What to watch for",
    headline: "Know which requests are yours",
    sections: [
      {
        h: "A burst of requests",
        body: "Three or four prompts in a few minutes, often at an odd moment. A real sign-in sends one, and only when you just tried to log in.",
      },
      {
        h: "A request you did not start",
        body: "If you are not signing in anywhere, there is nothing to approve. Tiredness and busy days are what the attacker is counting on.",
      },
      {
        h: "A number from someone else",
        body: "Number matching means you type the number shown on your own screen. A caller who reads out a number is using their own sign-in, not yours.",
      },
      {
        h: "A friendly call from IT",
        body: "A caller who knows your name and a real-looking number is not proof. Real IT never needs you to approve a request on their say-so.",
      },
    ],
    illustration: NumberMatchArt,
    caption: "Type only the number on the screen in front of you.",
  },
  {
    tab: "What to do",
    headline: "Deny, report, then check on your own",
    sections: [
      {
        h: "Deny every request you did not start",
        body: "Do not tap Approve to make it stop. Deny it, and keep denying if it keeps coming.",
      },
      {
        h: "Report it straight away",
        body: "Tell IT Security that someone tried to sign in as you. A report lets them reset your password and block the attacker before the next try.",
      },
      {
        h: "Check on a number you already know",
        body: "If someone calls, hang up and call the service desk on the number from the intranet. Do not use a number the caller gives you.",
      },
      {
        h: "Approve only your own sign-in",
        body: "When you did just sign in and the number matches, approving is right. The answer is not always no.",
      },
    ],
    illustration: DenyReportArt,
    caption: "Deny, report, then call IT on a number you trust.",
  },
];
